import { createClient } from '@supabase/supabase-js'

// ─── Конфигурация ─────────────────────────────────────────────────────────────
// Значения берутся из .env файла (НИКОГДА не хардкодить здесь).
// Для локальной разработки: создай .env в корне проекта.
// Для Vercel/Netlify/etc: добавь переменные в настройках деплоя.
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || ''
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY || ''

if (!SUPABASE_URL || !SUPABASE_KEY) {
  console.error('[Supabase] REACT_APP_SUPABASE_URL или REACT_APP_SUPABASE_KEY не заданы. Проверь .env файл.')
}

/**
 * URL Cloudflare Worker'а.
 * После деплоя worker.js добавь REACT_APP_WORKER_URL в .env или настройки деплоя.
 */
const WORKER_URL = process.env.REACT_APP_WORKER_URL || ''

// ─── Таймаут прокси (мс) ─────────────────────────────────────────────────────
const PROXY_TIMEOUT_MS = 3000

// ─── Ключ сессии ─────────────────────────────────────────────────────────────
const STORAGE_KEY = 'sb-qciqnfndgrrtoyzsbnvd-auth-token'

// ─── Состояние прокси ─────────────────────────────────────────────────────────
// Если Worker недоступен — переходим в режим прямого подключения до следующего
// перезапуска, чтобы не ждать таймаут на каждом запросе.
let _proxyAvailable = !!WORKER_URL   // false если WORKER_URL не задан
let _proxyCheckedAt  = 0
const PROXY_RECHECK_MS = 60_000      // пробуем снова через 60 сек

/**
 * Вспомогательная проверка: достижим ли Worker (GET /health).
 * Вызывается один раз при старте и после PROXY_RECHECK_MS неудач.
 */
const _checkProxy = async () => {
  if (!WORKER_URL) return false
  try {
    const ctrl = new AbortController()
    const id   = setTimeout(() => ctrl.abort(), 2000)
    const r    = await fetch(`${WORKER_URL}/health`, { signal: ctrl.signal })
    clearTimeout(id)
    return r.ok
  } catch {
    return false
  }
}

// Обычный запрос к базе или ко входу (не загрузка аудио) больше REQUEST_TIMEOUT_MS не ждём:
// на плохой мобильной сети fetch может висеть минутами, и всё, что ждёт ответа, стоит.
const REQUEST_TIMEOUT_MS = 30_000
const fetchWithTimeout = (url, options = {}) => {
  const urlStr = typeof url === 'string' ? url : url.toString()
  if (!/\/(rest|auth)\/v1\//.test(urlStr)) return fetch(url, options)
  const ctrl = new AbortController()
  const timerId = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS)
  const extSignal = options?.signal
  if (extSignal) {
    if (extSignal.aborted) ctrl.abort()
    else extSignal.addEventListener('abort', () => ctrl.abort(), { once: true })
  }
  return fetch(url, { ...options, signal: ctrl.signal }).finally(() => clearTimeout(timerId))
}

/**
 * Кастомный fetch: пробует Worker с таймаутом 3 сек,
 * при любой ошибке/таймауте — падает обратно на прямой Supabase.
 * Пользователь не видит никаких ошибок от этого переключения.
 */
const proxyFetch = async (url, options = {}) => {
  const urlStr = typeof url === 'string' ? url : url.toString()

  // Если Worker отключён или URL не относится к Supabase — прямой запрос
  if (!WORKER_URL || !urlStr.startsWith(SUPABASE_URL)) {
    return fetchWithTimeout(url, options)
  }

  // Периодически перепроверяем доступность прокси
  const now = Date.now()
  if (!_proxyAvailable && now - _proxyCheckedAt > PROXY_RECHECK_MS) {
    _proxyCheckedAt = now
    _proxyAvailable = await _checkProxy()
  }

  if (!_proxyAvailable) {
    return fetchWithTimeout(url, options)
  }

  // Подменяем origin: supabase.co → worker
  const proxyUrl = urlStr.replace(SUPABASE_URL, WORKER_URL)

  // Устанавливаем таймаут; совмещаем с существующим signal если есть
  const ctrl   = new AbortController()
  const timerId = setTimeout(() => ctrl.abort(), PROXY_TIMEOUT_MS)
  const extSignal = options?.signal
  if (extSignal) {
    extSignal.addEventListener('abort', () => ctrl.abort(), { once: true })
  }

  try {
    const resp = await fetch(proxyUrl, { ...options, signal: ctrl.signal })
    clearTimeout(timerId)
    return resp
  } catch (err) {
    clearTimeout(timerId)
    // Если это внешняя отмена — пробрасываем ошибку как есть
    if (extSignal?.aborted) throw err
    // Иначе — fallback без уведомления
    console.warn('[Proxy] недоступен, переключаюсь на прямой Supabase')
    _proxyAvailable = false
    _proxyCheckedAt = Date.now()
    return fetchWithTimeout(url, options)
  }
}

// ─── Ссылка «сбросить пароль» из письма ───────────────────────────────────────
// Письмо для сброса отправляет отдельный клиент без PKCE (см. requestPasswordReset):
// ссылка возвращает на сайт с токенами в #…&type=recovery, и её можно открыть в любом
// браузере и на любом устройстве. (С PKCE работало только в том же браузере, где
// нажали «Забыли пароль?», — из приложения на телефоне ссылка вела на обычный вход.)
// Забираем токены до того, как их увидит роутер приложения, и чистим адрес.
if (typeof window !== 'undefined' && /(^|[#&])(access_token|error_code)=/.test(window.location.hash)) {
  try {
    const h = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    if (h.get('type') === 'recovery' && h.get('access_token') && h.get('refresh_token')) {
      sessionStorage.setItem('ac_recovery', JSON.stringify({ access_token: h.get('access_token'), refresh_token: h.get('refresh_token') }))
    } else if (h.get('error_code') || h.get('error')) {
      sessionStorage.setItem('ac_auth_link_error', h.get('error_code') || h.get('error'))
    }
    window.history.replaceState(null, '', window.location.pathname + window.location.search + '#/projects')
  } catch { /* старый браузер — пусть разбирается библиотека */ }
}

// ─── Supabase клиент ──────────────────────────────────────────────────────────
export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    persistSession:    true,
    autoRefreshToken:  true,
    // Вход через Google возвращает на сайт с ?code=… — на вебе его надо прочитать.
    // В Capacitor-приложении — нельзя (обязательно false).
    detectSessionInUrl: !(typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()),
    flowType: 'pkce', // код в ?code=, а не токены в #hash — не мешает навигации по #/…
    storageKey: STORAGE_KEY,
  },
  global: {
    fetch: proxyFetch,           // все REST/Auth/Storage запросы идут через прокси
  },
})

// ─── Вспомогательная функция: перезаписать публичный URL на прокси ────────────
// Используется в storage.js для аудио-загрузок и getPublicUrl
export const toProxyUrl = (publicUrl) => {
  if (!WORKER_URL || !publicUrl) return publicUrl
  if (typeof publicUrl === 'string' && publicUrl.startsWith(SUPABASE_URL)) {
    return publicUrl.replace(SUPABASE_URL, WORKER_URL)
  }
  return publicUrl
}

// ─── Слушаем события авторизации ─────────────────────────────────────────────
supabase.auth.onAuthStateChange((event) => {
  if (event === 'TOKEN_REFRESHED') console.log('[Supabase] токен обновлён')
  if (event === 'SIGNED_OUT')      console.log('[Supabase] выход из аккаунта')
})

// ─── Health-check при старте: молча проверяем Worker ─────────────────────────
if (WORKER_URL) {
  _checkProxy().then((ok) => {
    _proxyAvailable = ok
    _proxyCheckedAt = Date.now()
    console.log(`[Proxy] ${ok ? 'активен ✓' : 'недоступен, прямое подключение'}`)
  })
}

// ─── Офлайн-безопасное получение сессии ──────────────────────────────────────
export const getSessionOfflineSafe = async () => {
  // Fast path: try Supabase client with 4s timeout — on Airplane mode getSession() hangs forever
  try {
    const sessionPromise = supabase.auth.getSession();
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 4000));
    const { data, error } = await Promise.race([sessionPromise, timeout]);
    if (!error && data?.session) return data.session;
  } catch {}

  // Offline fallback: parse stored token directly — works with no network at all
  const session = readStoredSession();
  if (!session) return null;
  const expiresAt = session.expires_at ?? 0;
  const nowSec    = Math.floor(Date.now() / 1000);
  // Token expired: fire background refresh but return expired session immediately.
  // Local data loading only needs session.user.id — a valid JWT is only needed for network ops.
  // The Supabase client will use the refreshed token automatically once it completes.
  if (expiresAt !== 0 && expiresAt <= nowSec) supabase.auth.refreshSession().catch(() => {});
  return session;
};

// Сохранённый вход — без сети и без ожидания. По нему приложение сразу открывает
// экран с данными устройства, а проверка входа на сервере идёт в фоне (раньше при
// истёкшем токене экран ждал ответа сервера до 4 с). null — входа на устройстве нет.
export const readStoredSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed  = JSON.parse(raw);
    const session = parsed?.access_token ? parsed : parsed?.session ?? null;
    // Must have both token and user.id (user.id is what we need for local data loading)
    if (!session?.access_token || !session?.user?.id) return null;
    return session;
  } catch {
    return null;
  }
};


// Куда ведут письма (сброс пароля, подтверждение почты). В приложении для телефона
// своего адреса нет (там https://localhost) — ведём на сайт.
export const WEB_ORIGIN = (typeof window !== 'undefined' && !window.Capacitor?.isNativePlatform?.()
  && /^https?:\/\//.test(window.location.origin)) ? window.location.origin : 'https://audiocards.vercel.app';

// Выход: на этом устройстве — сразу, даже без интернета; сервер забывает сеанс в фоне.
// (Раньше выход ждал ответа сервера — без сети кнопка «ничего не делала» до 30 с.)
export function signOutNow(accessToken) {
  if (accessToken) {
    try {
      fetch(`${SUPABASE_URL}/auth/v1/logout?scope=local`, {
        method: 'POST', keepalive: true,
        headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${accessToken}` },
      }).catch(() => {});
    } catch {}
  }
  return supabase.auth.signOut({ scope: 'local' }).catch(() => {});
}

// Письмо «сбросить пароль»: отдельный клиент без PKCE и без своей сессии — ссылка из
// письма откроется где угодно (см. разбор #…&type=recovery выше).
let _resetClient = null
export async function requestPasswordReset(email) {
  if (!_resetClient) {
    _resetClient = createClient(SUPABASE_URL, SUPABASE_KEY, {
      auth: { flowType: 'implicit', persistSession: false, autoRefreshToken: false, detectSessionInUrl: false, storageKey: 'ac-reset-client' },
      global: { fetch: proxyFetch },
    })
  }
  return _resetClient.auth.resetPasswordForEmail(String(email || '').trim(), { redirectTo: WEB_ORIGIN })
}
